import type { UserActivitySessionFilter, UserActivitySessionsDrilldown } from '../types';

function unknownOrEmpty(field: string): UserActivitySessionFilter {
  return [[field, '=', null], 'or', [field, '=', '']];
}

function equalsOrUnknown(field: string, key: string, label: string): UserActivitySessionsDrilldown {
  if (key === '__other__') {
    return { filter: null, label: `${label} (Other — open Sessions and refine)` };
  }
  if (key === 'Unknown' || key === '') {
    return { filter: unknownOrEmpty(field), label: `${label}: Unknown` };
  }
  return { filter: [field, '=', key], label: `${label}: ${key}` };
}

export function drilldownForUser(userKey: string, label: string): UserActivitySessionsDrilldown {
  if (!userKey || userKey === 'unknown') {
    return {
      filter: [
        ['userId', '=', null],
        'and',
        ['userName', '=', null]
      ],
      label: `User: ${label}`
    };
  }
  // Prefer userId equality; fall back to userName for non-guid keys.
  return {
    filter: [[ 'userId', '=', userKey ], 'or', [ 'userName', '=', userKey ]],
    label: `User: ${label}`
  };
}

export function drilldownForDimension(
  dimension:
    | 'lastPath'
    | 'role'
    | 'tenant'
    | 'endReason'
    | 'status'
    | 'device'
    | 'browser'
    | 'os'
    | 'build'
    | 'theme'
    | 'touch'
    | 'country'
    | 'continent'
    | 'asn'
    | 'asName'
    | 'asDomain'
    | 'timezone'
    | 'locale'
    | 'geoResolved',
  key: string,
  label: string
): UserActivitySessionsDrilldown {
  switch (dimension) {
    case 'lastPath':
      return equalsOrUnknown('lastPath', key, 'Last observed path');
    case 'role':
      if (key === '__other__') return { filter: null, label: 'Role: Other' };
      if (key === 'Unknown') return { filter: unknownOrEmpty('roles'), label: 'Role: Unknown' };
      return { filter: ['roles', 'contains', key], label: `Role: ${key}` };
    case 'tenant':
      return equalsOrUnknown('tenantId', key, 'Tenant');
    case 'endReason':
      return equalsOrUnknown('endReason', key, 'End reason');
    case 'status':
      if (key === 'Open') return { filter: ['endedAt', '=', null], label: 'Status: Open' };
      if (key === 'Completed') return { filter: ['endedAt', '<>', null], label: 'Status: Completed' };
      return { filter: null, label: `Status: ${label}` };
    case 'device':
      return equalsOrUnknown('deviceCategory', key, 'Device');
    case 'browser':
      return equalsOrUnknown('browser', key, 'Browser');
    case 'os':
      return equalsOrUnknown('os', key, 'OS');
    case 'build':
      return equalsOrUnknown('appBuild', key, 'Build');
    case 'theme': {
      if (key === '__other__') return { filter: null, label: 'Theme: Other' };
      if (key === 'Unknown/Unknown') {
        return {
          filter: [unknownOrEmpty('themeFamily'), 'and', unknownOrEmpty('themeMode')],
          label: 'Theme: Unknown'
        };
      }
      const [family, mode] = key.split('/');
      if (!family) return { filter: null, label: `Theme: ${label}` };
      if (!mode || mode === 'Unknown') {
        return { filter: ['themeFamily', '=', family], label: `Theme: ${label}` };
      }
      return {
        filter: [['themeFamily', '=', family], 'and', ['themeMode', '=', mode]],
        label: `Theme: ${label}`
      };
    }
    case 'touch':
      if (key === 'Touch') return { filter: ['maxTouchPoints', '>', 0], label: 'Touch capable' };
      if (key === 'No touch') {
        return {
          filter: [['maxTouchPoints', '=', null], 'or', ['maxTouchPoints', '=', 0]],
          label: 'No touch'
        };
      }
      return { filter: null, label: label };
    case 'country':
      return equalsOrUnknown('geoCountry', key, 'Country');
    case 'continent':
      return equalsOrUnknown('geoContinent', key, 'Continent');
    case 'asn':
      return equalsOrUnknown('geoAsn', key, 'ASN');
    case 'asName':
      return equalsOrUnknown('geoAsName', key, 'AS name');
    case 'asDomain':
      return equalsOrUnknown('geoAsDomain', key, 'AS domain');
    case 'timezone':
      return equalsOrUnknown('timezone', key, 'Timezone');
    case 'locale':
      return equalsOrUnknown('locale', key, 'Locale');
    case 'geoResolved':
      if (key === 'Resolved') return { filter: ['geoResolvedAt', '<>', null], label: 'Geo resolved' };
      if (key === 'Unresolved') return { filter: ['geoResolvedAt', '=', null], label: 'Geo unresolved' };
      return { filter: null, label };
    default:
      return { filter: null, label };
  }
}
